import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/Button/Button3";
import Seo from "../Seo/Seo";
import "./ResetPassword.css";
import homeBackground from '../../assets/images/home_background.avif';
import { ROUTES } from "../../utils/appRoutes";
import {
  buildAuthPathWithNext,
  getPostAuthFallbackPath,
  getSafeNextFromLocation,
} from "../../utils/authRedirect";
import {
  clearPasswordResetSession as clearStoredPasswordResetSession,
  loadPasswordResetSession,
  savePasswordResetSession,
} from "../../utils/passwordResetSession";

function ResetPassword({ onLogin }) {
  const [step, setStep] = useState(1);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // success or error
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [resetSessionToken, setResetSessionToken] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = getSafeNextFromLocation(location, getPostAuthFallbackPath(location));
  const loginPath = buildAuthPathWithNext(ROUTES.LOGIN, nextPath);
  const registerPath = buildAuthPathWithNext(ROUTES.REGISTER, nextPath);
  const forgotPasswordPath = buildAuthPathWithNext(ROUTES.FORGOT_PASSWORD, nextPath);

  const clearResetSession = () => {
    setResetSessionToken("");
    clearStoredPasswordResetSession();
  };

  const resetToVerificationStep = (nextEmail = "", nextCooldown = 0) => {
    clearResetSession();
    setStep(1);
    setConfirmationCode("");
    setNewPassword("");
    setShowPassword(false);
    setEmail(nextEmail);
    setResendCooldown(nextCooldown);
  };

  const saveTokens = ({ idToken, accessToken, refreshToken }) => {
    if (idToken) localStorage.setItem("idToken", idToken);
    if (accessToken) localStorage.setItem("accessToken", accessToken);
    if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
    if (typeof onLogin === "function") {
      onLogin(idToken, accessToken, refreshToken);
    }
  };

  useEffect(() => {
    const requestedEmail = location.state?.email || "";
    const hasFreshResetRequest = Boolean(location.state?.resetRequestId);

    if (hasFreshResetRequest) {
      clearStoredPasswordResetSession();
      setResetSessionToken("");
      setStep(1);
      setConfirmationCode("");
      setNewPassword("");
      setShowPassword(false);
      setEmail(requestedEmail);
      setResendCooldown(requestedEmail ? 30 : 0);
      setMessage("");
      setMessageType("");
      return;
    }

    const storedSession = loadPasswordResetSession();
    if (storedSession) {
      setResetSessionToken(storedSession.resetSessionToken);
      setEmail(storedSession.email);
      setStep(2);
      return;
    }

    if (!requestedEmail) return;
    clearStoredPasswordResetSession();
    setResetSessionToken("");
    setStep(1);
    setConfirmationCode("");
    setNewPassword("");
    setShowPassword(false);
    setEmail(requestedEmail);
    setResendCooldown(30);
  }, [location.state?.email, location.state?.resetRequestId]);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleEmailChange = (event) => {
    const nextEmail = event.target.value;
    const emailChanged = nextEmail !== email;

    if (emailChanged) {
      clearResetSession();
      setConfirmationCode("");
      setNewPassword("");
      setResendCooldown(0);
      setMessage("");
      setMessageType("");
    }

    setEmail(nextEmail);
  };

  const handleStartOver = () => {
    resetToVerificationStep(email, 0);
    setMessage("Enter the newest confirmation code to continue.");
    setMessageType("success");
  };

  const handleNextStep = async () => {
    if (!email || !confirmationCode) {
      setMessage("Please provide your email and confirmation code.");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");
    setMessageType("");

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/verify-reset-code`, {
        email,
        confirmationCode,
      });

      const nextResetSessionToken = response.data?.resetSessionToken;
      if (!nextResetSessionToken) {
        setMessage("Could not verify the reset code. Please request a new one.");
        setMessageType("error");
        return;
      }

      setResetSessionToken(nextResetSessionToken);
      savePasswordResetSession({
        email: response.data?.email || email,
        resetSessionToken: nextResetSessionToken,
      });
      setEmail(response.data?.email || email);
      setStep(2);
      setMessage("");
      setMessageType("");
    } catch (error) {
      setMessage(error.response?.data?.error || "Failed to verify reset code.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetSessionToken || !newPassword) {
      setMessage("Please fill in all fields.");
      setMessageType("error");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/reset-password`, {
        newPassword,
        resetSessionToken,
      });
      clearResetSession();
      if (response.data?.idToken && response.data?.accessToken) {
        saveTokens(response.data);
        setMessage("Password reset and login successful.");
        setMessageType("success");
        navigate(nextPath, { replace: true });
        return;
      }

      setMessage(response.data.message || "Password reset successfully. Please log in.");
      setMessageType("success");
      setTimeout(() => navigate(loginPath), 2000);
    } catch (error) {
      if (error.response?.data?.error === "Reset session expired or invalid. Please request a new code.") {
        resetToVerificationStep(email, 0);
      }
      setMessage(error.response?.data?.error || "Failed to reset password.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (isResendingCode || resendCooldown > 0) return;
    if (!email) {
      setMessage("Enter your email first to resend the reset code.");
      setMessageType("error");
      return;
    }

    setIsResendingCode(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/forgot-password`, {
        email,
      });

      resetToVerificationStep(email, 30);
      setResendCooldown(30);
      setMessage(
        `${response.data?.message ||
          "Password reset code sent. Please check your inbox and spam folder."} Use the newest code to continue.`
      );
      setMessageType("success");
    } catch (error) {
      setMessage(error.response?.data?.error || "Failed to resend reset code.");
      setMessageType("error");
    } finally {
      setIsResendingCode(false);
    }
  };

  return (
    <>
      <Seo
        title="Reset Password | ZealFC"
        description="Reset your ZealFC account password using your confirmation code."
        robots="noindex, nofollow"
        canonicalPath={ROUTES.RESET_PASSWORD}
      />
      <div className="reset-password-layout">
        <div className="reset-password-image-container">
          <img src={homeBackground} alt="Background" className="reset-password-image" />
          <div className="noise-effect"></div>
        </div>
        <div className="reset-password-form-container">
          <p className="reset-password-title">
            {step === 1 ? "ENTER CONFIRMATION CODE" : "RESET YOUR PASSWORD"}
          </p>
          <form className="reset-password-form">
            {step === 1 && (
              <>
                <div className="reset-password-field">
                  <span>Email:</span>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={handleEmailChange}
                    className="reset-password-input-line"
                  />
                </div>
                <div className="reset-password-field">
                  <span>Confirmation Code:</span>
                  <input
                    type="text"
                    placeholder="Enter confirmation code"
                    value={confirmationCode}
                    onChange={(e) => setConfirmationCode(e.target.value)}
                    className="reset-password-input-line"
                  />
                </div>
                {email && (
                  <div className="reset-password-resend-row">
                    <button
                      type="button"
                      className="reset-password-resend-link"
                      onClick={handleResendCode}
                      disabled={isResendingCode || resendCooldown > 0}
                    >
                      {isResendingCode
                        ? "Sending..."
                        : resendCooldown > 0
                          ? `Resend Code (${resendCooldown}s)`
                          : "Resend Code"}
                    </button>
                  </div>
                )}
                <Button
                  onClick={handleNextStep}
                  variant="small"
                  primaryText={loading ? "Verifying..." : "Continue"}
                  disabled={loading}
                />
              </>
            )}
            {step === 2 && (
              <>
                <div className="reset-password-field">
                  <span>Email:</span>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    disabled
                    className="reset-password-input-line"
                  />
                </div>
                <div className="reset-password-resend-row">
                  <button
                    type="button"
                    className="reset-password-resend-link"
                    onClick={handleStartOver}
                  >
                    Use a different code
                  </button>
                </div>
                <div className="reset-password-field">
                  <span>New Password:</span>
                  <div className="password-container">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="reset-password-input-line"
                    />
                    <span
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={handleResetPassword}
                  variant="small"
                  primaryText={loading ? "Resetting..." : "Reset"}
                  disabled={loading}
                />
              </>
            )}
          </form>
          {message && (
            <p className={`reset-password-message ${messageType}`}>
              {message}
            </p>
          )}
          <div className="login-links">
            <div className="login-link">
              <span>Don't have an account?</span>
              <Link to={registerPath} className="login-link-text"> Register</Link>
            </div>
            <div className="login-link">
              <span>Already have an account?</span>
              <Link to={loginPath} className="login-link-text"> Login</Link>
            </div>
            <div className="login-link">
              <span>Forgot your password?</span>
              <Link to={forgotPasswordPath} className="login-link-text"> Reset Password</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ResetPassword;
